import React, { useEffect, useState } from 'react';
import { useAuth } from './context/auth/AuthContext';
import LandingPage from './sections/landing/LandingPage';
import DashboardLayout from './components/layout/DashboardLayout';
import AgentDetails from './sections/dashboard/AgentDetails';
import RewardsPage from './sections/rewards/RewardsPage';
import AdminDashboard from './sections/dashboard/AdminDashboard';

const MainApp = () => {
  const { isUserLoggedIn } = useAuth();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [activePage, setActivePage] = useState(() => {
    // Try to restore active page from localStorage on component mount
    const savedPage = localStorage.getItem('chatbotActivePage');
    
    // Also check URL hash for page navigation
    const urlHash = window.location.hash.replace('#', '');
    const validPages = ['Home', /* 'Awards & Winners', */ 'Knowledge Base', 'Phone Numbers', /* 'Verified Numbers', */ 'Batch Call', 'Call History', /* 'Assistant Chat History', */ 'Analytics', 'Billing', /* 'Billing V1', */ 'Admin Dashboard', 'Sign Up People', 'Documentation', 'Promo Code', 'Retell Agents Details', 'Deleted Records', 'Setup Billing']; // Awards & Winners, Verified Numbers, Assistant Chat History COMMENTED OUT
    
    if (urlHash && validPages.includes(urlHash)) {
      return urlHash;
    }
    
    return savedPage || 'Home';
  });
  const [authLoaded, setAuthLoaded] = useState(false);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    const handleHashChange = () => {
      const urlHash = window.location.hash.replace('#', '');
      const validPages = ['Home', /* 'Awards & Winners', */ 'Knowledge Base', 'Phone Numbers', /* 'Verified Numbers', */ 'Batch Call', 'Call History', /* 'Assistant Chat History', */ 'Analytics', 'Billing', /* 'Billing V1', */ 'Admin Dashboard', 'Sign Up People', 'Documentation', 'Promo Code', 'Retell Agents Details', 'Deleted Records', 'Setup Billing']; // Awards & Winners, Verified Numbers, Assistant Chat History COMMENTED OUT
      
      if (urlHash && validPages.includes(urlHash)) {
        setActivePage(urlHash);
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Save active page to localStorage whenever it changes
  useEffect(() => {
    if (isUserLoggedIn) {
      localStorage.setItem('chatbotActivePage', activePage);
      // Update URL hash to reflect current page
      if (window.location.hash !== `#${activePage}`) {
        window.history.replaceState(null, '', `#${activePage}`);
      }
    }
  }, [activePage, isUserLoggedIn]);

  // Wait for auth to load before rendering/redirecting
  useEffect(() => {
    // Simulate async auth check (localStorage is sync, but this ensures we wait for the first render)
    setAuthLoaded(true);
  }, []);

  if (!authLoaded) {
    // Optionally show a loading spinner here
    return null;
  }

  // If user is not logged in and trying to access protected routes, redirect to landing
  if (!isUserLoggedIn && (currentPath === '/dashboard' || currentPath.startsWith('/agent/'))) {
    window.history.pushState({}, '', '/');
    return <LandingPage />;
  }

  // Render Rewards page if path matches /rewards
  if (currentPath === '/rewards') {
    return <RewardsPage />;
  }

  // If user is logged in and on landing page, redirect to dashboard
  if (isUserLoggedIn && currentPath === '/') {
    // All users (including admin) redirect to dashboard
    window.history.pushState({}, '', '/dashboard');
    return <DashboardLayout activePage={activePage} onPageChange={setActivePage} />;
  }

  // Render AgentDetails page if path matches /agent/:id
  if (isUserLoggedIn && currentPath.startsWith('/agent/')) {
    const agentId = currentPath.split('/agent/')[1];
    return <AgentDetails agentId={agentId} />;
  }

  // Render based on authentication status
  if (isUserLoggedIn) {
    // All users (including admin) use the same dashboard layout
    return <DashboardLayout activePage={activePage} onPageChange={setActivePage} />;
  } else {
    return <LandingPage />;
  }
};

export default MainApp; 